import { Public, LocationCity, Map } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  CssBaseline,
  Divider,
  Grid,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import Topbar from "../../components/topbar/TopBar";
import { getCountryListUseCase } from "./country/domain/usecases/getCountryListUseCase";
import { getDepartmentListByCountryIdUseCase } from "./department/domain/usecases/getDepartmentListByCountryIdUseCase";
import { getProvinceListByDepartmentIdUseCase } from "./province/domain/usecases/getProvinceListByDepartmentIdUseCase";

const SECTIONS = [
  {
    title: "PAISES",
    icon: Public,
    description: "Administra los registros principales de paises.",
    badge: "SECCION 1",
    createUrl: "/admin/geography/country",
  },
  {
    title: "DEPARTAMENTOS",
    icon: LocationCity,
    description: "Organiza los departamentos asociados a cada pais.",
    badge: "SECCION 2",
    createUrl: "/admin/geography/department",
  },
  {
    title: "PROVINCIAS",
    icon: Map,
    description: "Configura las provincias y sus opciones operativas.",
    badge: "SECCION 3",
    createUrl: "/admin/geography/province",
  },
];

export const GeographyCatalogPanelPage = () => {
  const navigate = useNavigate();
  const [countries, setCountries] = useState([]);
  const [loadingCountries, setLoadingCountries] = useState(true);
  const [countryError, setCountryError] = useState("");
  const [departments, setDepartments] = useState([]);
  const [loadingDepartments, setLoadingDepartments] = useState(false);
  const [departmentError, setDepartmentError] = useState("");
  const [selectedCountryId, setSelectedCountryId] = useState(null);
  const [provinces, setProvinces] = useState([]);
  const [loadingProvinces, setLoadingProvinces] = useState(false);
  const [provinceError, setProvinceError] = useState("");
  const [selectedDepartmentId, setSelectedDepartmentId] = useState(null);

  useEffect(() => {
    const loadCountries = async () => {
      try {
        setLoadingCountries(true);
        setCountryError("");
        setCountries(await getCountryListUseCase());
      } catch (error) {
        setCountryError(error.message || "No se pudo cargar la lista de paises.");
      } finally {
        setLoadingCountries(false);
      }
    };

    loadCountries();
  }, []);

  const orderedCountries = useMemo(
    () => [...countries].sort((left, right) => Number(left.id) - Number(right.id)),
    [countries],
  );

  const orderedDepartments = useMemo(
    () =>
      [...departments].sort((left, right) => Number(left.id) - Number(right.id)),
    [departments],
  );

  const orderedProvinces = useMemo(
    () => [...provinces].sort((left, right) => Number(left.id) - Number(right.id)),
    [provinces],
  );

  const handleLoadDepartments = async (countryId) => {
    try {
      setLoadingDepartments(true);
      setDepartmentError("");
      setSelectedCountryId(countryId);
      setSelectedDepartmentId(null);
      setProvinces([]);
      setProvinceError("");
      setDepartments(await getDepartmentListByCountryIdUseCase(countryId));
    } catch (error) {
      setDepartmentError(
        error.message || "No se pudo cargar la lista de departamentos.",
      );
      setDepartments([]);
      setProvinces([]);
    } finally {
      setLoadingDepartments(false);
    }
  };

  const handleLoadProvinces = async (departmentId) => {
    try {
      setLoadingProvinces(true);
      setProvinceError("");
      setSelectedDepartmentId(departmentId);
      setProvinces(await getProvinceListByDepartmentIdUseCase(departmentId));
    } catch (error) {
      setProvinceError(error.message || "No se pudo cargar la lista de provincias.");
      setProvinces([]);
    } finally {
      setLoadingProvinces(false);
    }
  };

  return (
    <>
      <CssBaseline />
      <main className="content">
        <Topbar />
        <Box
          sx={{
            minHeight: "100vh",
            color: "#000",
            "& .MuiTypography-root": { color: "#000" },
            p: 1,
          }}
        >
          <Stack spacing={3}>
            <Paper
              elevation={0}
              sx={{
                width: "100%",
                borderRadius: 3,
                overflow: "hidden",
                border: "1px solid rgba(16, 24, 40, 0.08)",
              }}
            >
              <Box
                sx={{
                  px: { xs: 2, md: 3 },
                  py: { xs: 2.5, md: 3 },
                  background:
                    "linear-gradient(135deg, #0f3d3e 0%, #165c53 55%, #2d8b74 100%)",
                }}
              >
                <Stack spacing={1}>
                  <Typography
                    variant="h4"
                    fontWeight={800}
                    sx={{ color: "#fff !important" }}
                  >
                    LISTA DE PAISES
                  </Typography>
                  <Typography
                    sx={{ color: "#fff !important", opacity: 0.88, maxWidth: 720 }}
                  >
                    Panel principal para la gestion de paises, departamentos y
                    provincias.
                  </Typography>
                </Stack>
              </Box>

              <Box sx={{ px: { xs: 2, md: 3 }, py: 2, bgcolor: "#fff" }}>
                <Typography variant="body1" fontWeight={600}>
                  Catalogo geografico
                </Typography>
              </Box>
            </Paper>

            <Grid container spacing={1}>
              {SECTIONS.map((section) => {
                const Icon = section.icon;

                return (
                  <Grid item xs={12} md={4} key={section.title} padding={2}>
                    <Paper
                      elevation={0}
                      sx={{
                        height: "100%",
                        minHeight: 280,
                        borderRadius: 3,
                        border: "1px solid rgba(16, 24, 40, 0.08)",
                        overflow: "hidden",
                        background:
                          "linear-gradient(180deg, #ffffff 0%, #f8fbfa 100%)",
                      }}
                    >
                      <Box
                        sx={{
                          px: { xs: 1.5, md: 2.5 },
                          py: 2,
                          background:
                            "linear-gradient(135deg, rgba(15,61,62,0.08) 0%, rgba(45,139,116,0.14) 100%)",
                        }}
                      >
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          alignItems="center"
                          spacing={1.5}
                        >
                          <Stack direction="row" spacing={1.5} alignItems="center">
                            <Box
                              sx={{
                                width: 52,
                                height: 52,
                                borderRadius: 2,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                bgcolor: "#165c53",
                              }}
                            >
                              <Icon sx={{ color: "#fff", fontSize: 28 }} />
                            </Box>
                            <Box>
                              <Typography variant="h6" fontWeight={800}>
                                {section.title}
                              </Typography>
                              <Typography variant="body2" sx={{ color: "#000" }}>
                                {section.badge}
                              </Typography>
                            </Box>
                          </Stack>

                          <Button
                            variant="contained"
                            onClick={() => navigate(section.createUrl)}
                            sx={{
                              borderRadius: 2,
                              textTransform: "none",
                              fontWeight: 700,
                              bgcolor: "#165c53",
                              color: "#fff",
                              minWidth: { xs: 92, sm: 104 },
                              "&:hover": {
                                bgcolor: "#12483f",
                              },
                            }}
                          >
                            Agregar
                          </Button>
                        </Stack>
                      </Box>

                      <Divider />

                      <Box sx={{ p: { xs: 1.5, md: 2.5 } }}>
                        <Stack spacing={2.5}>
                          <Typography variant="body1" sx={{ color: "#000" }}>
                            {section.description}
                          </Typography>

                          {section.title === "PAISES" ? (
                            <>
                              {countryError ? (
                                <Alert severity="error">{countryError}</Alert>
                              ) : null}

                              {loadingCountries ? (
                                <Stack alignItems="center" spacing={2} sx={{ py: 4 }}>
                                  <CircularProgress />
                                  <Typography variant="body2" sx={{ color: "#000" }}>
                                    Cargando paises...
                                  </Typography>
                                </Stack>
                              ) : null}

                              {!loadingCountries && orderedCountries.length === 0 ? (
                                <Paper
                                  elevation={0}
                                  sx={{
                                    borderRadius: 2.5,
                                    border: "1px dashed rgba(22, 92, 83, 0.28)",
                                    bgcolor: "#fbfcfc",
                                    p: 2,
                                  }}
                                >
                                  <Typography variant="body2" fontWeight={700}>
                                    Sin registros
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    sx={{ color: "#000", mt: 0.75 }}
                                  >
                                    No hay paises disponibles para mostrar.
                                  </Typography>
                                </Paper>
                              ) : null}

                              {!loadingCountries &&
                              orderedCountries.length > 0 ? (
                                <Stack spacing={1.5}>
                                  {orderedCountries.map((country) => (
                                    <Paper
                                      key={country.id}
                                      elevation={0}
                                      sx={{
                                        width: "100%",
                                        minWidth: 0,
                                        borderRadius: 2.5,
                                        border: "1px solid rgba(20, 83, 45, 0.12)",
                                        bgcolor: "#fff",
                                        p: 2,
                                      }}
                                    >
                                      <Stack spacing={1.5}>
                                        <Stack
                                          direction={{ xs: "column", sm: "row" }}
                                          justifyContent="space-between"
                                          alignItems={{ xs: "flex-start", sm: "center" }}
                                          spacing={1}
                                        >
                                          <Typography variant="body2" fontWeight={800}>
                                            ID: {country.id}
                                          </Typography>
                                          <Chip
                                            size="small"
                                            label={country.code}
                                            sx={{
                                              bgcolor: "#edf7f3",
                                              color: "#165c53",
                                              fontWeight: 800,
                                            }}
                                          />
                                        </Stack>

                                        <Typography
                                          variant="body1"
                                          fontWeight={700}
                                          sx={{ wordBreak: "break-word" }}
                                        >
                                          {country.name}
                                        </Typography>

                                        <Typography variant="body2" sx={{ color: "#000" }}>
                                          Codigo: {country.code}
                                        </Typography>

                                        <Button
                                          variant="contained"
                                          onClick={() =>
                                            navigate(
                                              `/admin/geography/country/update/${country.id}`,
                                            )
                                          }
                                          sx={{
                                            alignSelf: { xs: "stretch", sm: "flex-start" },
                                            borderRadius: 2,
                                            textTransform: "none",
                                            fontWeight: 700,
                                            bgcolor: "#165c53",
                                            color: "#fff",
                                            "&:hover": {
                                              bgcolor: "#12483f",
                                            },
                                          }}
                                        >
                                          Actualizar
                                        </Button>

                                        <Button
                                          variant="outlined"
                                          onClick={() => handleLoadDepartments(country.id)}
                                          sx={{
                                            alignSelf: { xs: "stretch", sm: "flex-start" },
                                            borderRadius: 2,
                                            textTransform: "none",
                                            fontWeight: 700,
                                            borderColor: "#165c53",
                                            color: "#165c53",
                                            "&:hover": {
                                              borderColor: "#12483f",
                                              bgcolor: "rgba(22,92,83,0.06)",
                                            },
                                          }}
                                        >
                                          DEPARTAMENTOS
                                        </Button>
                                      </Stack>
                                    </Paper>
                                  ))}
                                </Stack>
                              ) : null}
                            </>
                          ) : section.title === "DEPARTAMENTOS" ? (
                            <>
                              {departmentError ? (
                                <Alert severity="error">{departmentError}</Alert>
                              ) : null}

                              {!selectedCountryId && !loadingDepartments ? (
                                <Paper
                                  elevation={0}
                                  sx={{
                                    borderRadius: 2.5,
                                    border: "1px dashed rgba(22, 92, 83, 0.28)",
                                    bgcolor: "#fbfcfc",
                                    p: 2,
                                  }}
                                >
                                  <Typography variant="body2" fontWeight={700}>
                                    Sin seleccion
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    sx={{ color: "#000", mt: 0.75 }}
                                  >
                                    Selecciona un pais para cargar sus
                                    departamentos.
                                  </Typography>
                                </Paper>
                              ) : null}

                              {loadingDepartments ? (
                                <Stack alignItems="center" spacing={2} sx={{ py: 4 }}>
                                  <CircularProgress />
                                  <Typography variant="body2" sx={{ color: "#000" }}>
                                    Cargando departamentos...
                                  </Typography>
                                </Stack>
                              ) : null}

                              {!loadingDepartments &&
                              selectedCountryId &&
                              orderedDepartments.length === 0 &&
                              !departmentError ? (
                                <Paper
                                  elevation={0}
                                  sx={{
                                    borderRadius: 2.5,
                                    border: "1px dashed rgba(22, 92, 83, 0.28)",
                                    bgcolor: "#fbfcfc",
                                    p: 2,
                                  }}
                                >
                                  <Typography variant="body2" fontWeight={700}>
                                    Sin registros
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    sx={{ color: "#000", mt: 0.75 }}
                                  >
                                    No hay departamentos para el pais
                                    seleccionado.
                                  </Typography>
                                </Paper>
                              ) : null}

                              {!loadingDepartments &&
                              orderedDepartments.length > 0 ? (
                                <Stack spacing={1.5}>
                                  {orderedDepartments.map((department) => (
                                    <Paper
                                      key={department.id}
                                      elevation={0}
                                      sx={{
                                        width: "100%",
                                        minWidth: 0,
                                        borderRadius: 2.5,
                                        border: "1px solid rgba(20, 83, 45, 0.12)",
                                        bgcolor: "#fff",
                                        p: 2,
                                      }}
                                    >
                                      <Stack spacing={1.5}>
                                        <Stack
                                          direction={{ xs: "column", sm: "row" }}
                                          justifyContent="space-between"
                                          alignItems={{ xs: "flex-start", sm: "center" }}
                                          spacing={1}
                                        >
                                          <Typography variant="body2" fontWeight={800}>
                                            ID: {department.id}
                                          </Typography>
                                          <Chip
                                            size="small"
                                            label={department.code}
                                            sx={{
                                              bgcolor: "#edf7f3",
                                              color: "#165c53",
                                              fontWeight: 800,
                                            }}
                                          />
                                        </Stack>

                                        <Typography
                                          variant="body1"
                                          fontWeight={700}
                                          sx={{ wordBreak: "break-word" }}
                                        >
                                          {department.name}
                                        </Typography>

                                        <Typography variant="body2" sx={{ color: "#000" }}>
                                          Codigo: {department.code}
                                        </Typography>

                                        <Stack
                                          direction={{ xs: "column", sm: "row" }}
                                          spacing={1.25}
                                          sx={{ width: "100%" }}
                                        >
                                          <Button
                                            variant="contained"
                                            onClick={() =>
                                              navigate(
                                                `/admin/geography/department/update/${department.id}`,
                                              )
                                            }
                                            sx={{
                                              width: { xs: "100%", sm: "auto" },
                                              borderRadius: 2,
                                              textTransform: "none",
                                              fontWeight: 700,
                                              bgcolor: "#165c53",
                                              color: "#fff",
                                              "&:hover": {
                                                bgcolor: "#12483f",
                                              },
                                            }}
                                          >
                                            Actualizar
                                          </Button>

                                          <Button
                                            variant="outlined"
                                            onClick={() =>
                                              handleLoadProvinces(department.id)
                                            }
                                            sx={{
                                              width: { xs: "100%", sm: "auto" },
                                              borderRadius: 2,
                                              textTransform: "none",
                                              fontWeight: 700,
                                              borderColor: "#165c53",
                                              color: "#165c53",
                                              "&:hover": {
                                                borderColor: "#12483f",
                                                bgcolor: "rgba(22,92,83,0.06)",
                                              },
                                            }}
                                          >
                                            Provincias
                                          </Button>
                                        </Stack>
                                      </Stack>
                                    </Paper>
                                  ))}
                                </Stack>
                              ) : null}
                            </>
                          ) : section.title === "PROVINCIAS" ? (
                            <>
                              {provinceError ? (
                                <Alert severity="error">{provinceError}</Alert>
                              ) : null}

                              {!selectedDepartmentId && !loadingProvinces ? (
                                <Paper
                                  elevation={0}
                                  sx={{
                                    borderRadius: 2.5,
                                    border: "1px dashed rgba(22, 92, 83, 0.28)",
                                    bgcolor: "#fbfcfc",
                                    p: 2,
                                  }}
                                >
                                  <Typography variant="body2" fontWeight={700}>
                                    Sin seleccion
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    sx={{ color: "#000", mt: 0.75 }}
                                  >
                                    Selecciona un departamento para cargar sus
                                    provincias.
                                  </Typography>
                                </Paper>
                              ) : null}

                              {loadingProvinces ? (
                                <Stack alignItems="center" spacing={2} sx={{ py: 4 }}>
                                  <CircularProgress />
                                  <Typography variant="body2" sx={{ color: "#000" }}>
                                    Cargando provincias...
                                  </Typography>
                                </Stack>
                              ) : null}

                              {!loadingProvinces &&
                              selectedDepartmentId &&
                              orderedProvinces.length === 0 &&
                              !provinceError ? (
                                <Paper
                                  elevation={0}
                                  sx={{
                                    borderRadius: 2.5,
                                    border: "1px dashed rgba(22, 92, 83, 0.28)",
                                    bgcolor: "#fbfcfc",
                                    p: 2,
                                  }}
                                >
                                  <Typography variant="body2" fontWeight={700}>
                                    Sin registros
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    sx={{ color: "#000", mt: 0.75 }}
                                  >
                                    No hay provincias para el departamento
                                    seleccionado.
                                  </Typography>
                                </Paper>
                              ) : null}

                              {!loadingProvinces && orderedProvinces.length > 0 ? (
                                <Stack spacing={1.5}>
                                  {orderedProvinces.map((province) => (
                                    <Paper
                                      key={province.id}
                                      elevation={0}
                                      sx={{
                                        width: "100%",
                                        minWidth: 0,
                                        borderRadius: 2.5,
                                        border: "1px solid rgba(20, 83, 45, 0.12)",
                                        bgcolor: "#fff",
                                        p: { xs: 1.5, sm: 2 },
                                      }}
                                    >
                                      <Stack spacing={1.5}>
                                        <Stack
                                          direction={{ xs: "column", sm: "row" }}
                                          justifyContent="space-between"
                                          alignItems={{ xs: "flex-start", sm: "center" }}
                                          spacing={1}
                                        >
                                          <Typography variant="body2" fontWeight={800}>
                                            ID: {province.id}
                                          </Typography>
                                          <Chip
                                            size="small"
                                            label={province.code}
                                            sx={{
                                              bgcolor: "#edf7f3",
                                              color: "#165c53",
                                              fontWeight: 800,
                                            }}
                                          />
                                        </Stack>

                                        <Typography
                                          variant="body1"
                                          fontWeight={700}
                                          sx={{ wordBreak: "break-word" }}
                                        >
                                          {province.name}
                                        </Typography>

                                        <Typography
                                          variant="body2"
                                          sx={{ color: "#000", wordBreak: "break-word" }}
                                        >
                                          Codigo: {province.code}
                                        </Typography>

                                        <Typography variant="body2" sx={{ color: "#000" }}>
                                          Calcular precio:{" "}
                                          {province.calculatePrice ? "Si" : "No"}
                                        </Typography>

                                        <Typography variant="body2" sx={{ color: "#000" }}>
                                          Reserva: {province.reservation ? "Si" : "No"}
                                        </Typography>

                                        <Button
                                          variant="contained"
                                          onClick={() =>
                                            navigate(
                                              `/admin/geography/province/update/${province.id}`,
                                            )
                                          }
                                          sx={{
                                            alignSelf: { xs: "stretch", sm: "flex-start" },
                                            borderRadius: 2,
                                            textTransform: "none",
                                            fontWeight: 700,
                                            bgcolor: "#165c53",
                                            color: "#fff",
                                            "&:hover": {
                                              bgcolor: "#12483f",
                                            },
                                          }}
                                        >
                                          Actualizar
                                        </Button>
                                      </Stack>
                                    </Paper>
                                  ))}
                                </Stack>
                              ) : null}
                            </>
                          ) : (
                            <>
                              <Paper
                                elevation={0}
                                sx={{
                                  borderRadius: 2.5,
                                  border: "1px solid rgba(20, 83, 45, 0.12)",
                                  bgcolor: "#fff",
                                  p: 2,
                                }}
                              >
                                <Stack spacing={1}>
                                  <Typography variant="body2" fontWeight={700}>
                                    Estado actual
                                  </Typography>
                                  <Typography variant="body2" sx={{ color: "#000" }}>
                                    Seccion preparada para listar y administrar
                                    registros.
                                  </Typography>
                                </Stack>
                              </Paper>

                              <Paper
                                elevation={0}
                                sx={{
                                  borderRadius: 2.5,
                                  border: "1px dashed rgba(22, 92, 83, 0.28)",
                                  bgcolor: "#fbfcfc",
                                  p: 2,
                                }}
                              >
                                <Stack spacing={1}>
                                  <Typography variant="body2" fontWeight={700}>
                                    Proximo contenido
                                  </Typography>
                                  <Typography variant="body2" sx={{ color: "#000" }}>
                                    Aqui se puede incorporar la lista, filtros y
                                    acciones del modulo.
                                  </Typography>
                                </Stack>
                              </Paper>
                            </>
                          )}
                        </Stack>
                      </Box>
                    </Paper>
                  </Grid>
                );
              })}
            </Grid>
          </Stack>
        </Box>
      </main>
    </>
  );
};

export default GeographyCatalogPanelPage;
